import { useState } from "react";
import { KeyRound, ShieldCheck, ShieldOff, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_HINTS } from "@/lib/roles";

const box = "rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-4";

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Le due password nuove non coincidono");
    setBusy(true);
    try {
      await api.put("/auth/password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password cambiata. Gli altri dispositivi sono stati disconnessi.");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className={box} data-testid="password-card">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[var(--primary)]" /> Cambia password</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Password attuale</Label><Input type="password" required autoComplete="current-password" value={f.current_password} onChange={set("current_password")} data-testid="current-password-input" /></div>
        <div className="space-y-1.5"><Label>Nuova (min 10)</Label><Input type="password" required minLength={10} autoComplete="new-password" value={f.new_password} onChange={set("new_password")} data-testid="new-password-input" /></div>
        <div className="space-y-1.5"><Label>Ripeti nuova</Label><Input type="password" required minLength={10} autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="confirm-password-input" /></div>
      </div>
      <button disabled={busy} className="btn-primary h-11 px-5" data-testid="change-password-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva nuova password"}</button>
    </form>
  );
}

function RecoveryCodes({ codes }) {
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Codici copiati"); };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="recovery-codes">
      <p className="text-sm font-semibold">Codici di recupero: salvali ora, non verranno più mostrati.</p>
      <p className="text-xs">Ognuno funziona una sola volta, al posto del codice dell'app, se perdi il telefono.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm">{codes.map((c) => <span key={c} className="bg-white rounded px-2 py-1 text-center">{c}</span>)}</div>
      <button type="button" onClick={copy} className="btn-secondary h-9 px-3 text-sm" data-testid="copy-recovery-codes"><Copy className="h-4 w-4" /> Copia</button>
    </div>
  );
}

function TwoFactorCard() {
  const { user, reload } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [codes, setCodes] = useState(null);
  const start = async () => { try { setSetup((await api.post("/auth/2fa/setup")).data); } catch (e) { toast.error(formatApiError(e)); } };
  const enable = async () => {
    try { setCodes((await api.post("/auth/2fa/enable", { code })).data.recovery_codes); setSetup(null); setCode(""); toast.success("Verifica in due passaggi attivata"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { code, password: pw }); setCode(""); setPw(""); toast.success("Verifica in due passaggi disattivata"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className={box} data-testid="twofa-card">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[var(--primary)]" /> Verifica in due passaggi</h2>
      <p className="text-sm text-[var(--muted)]">All'accesso, oltre alla password, serve un codice a 6 cifre dall'app gratuita sul telefono (Google Authenticator, Microsoft Authenticator o simili).{user.role === "admin" && " Fortemente consigliata per l'amministratore."}</p>
      {codes && <RecoveryCodes codes={codes} />}
      {user.totp_enabled ? (
        <div className="space-y-3" data-testid="twofa-enabled">
          <p className="text-sm font-semibold text-emerald-700">Attiva</p>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="twofa-disable-password-input" /></div>
            <div className="space-y-1.5"><Label>Codice dall'app</Label><Input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-disable-code-input" /></div>
            <button onClick={disable} className="btn-secondary h-10 px-4 text-red-600" data-testid="twofa-disable-button"><ShieldOff className="h-4 w-4" /> Disattiva</button>
          </div>
        </div>
      ) : setup ? (
        <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-start" data-testid="twofa-setup">
          <img src={setup.qr} alt="Codice QR" className="w-44 h-44 rounded-xl border" data-testid="twofa-qr" />
          <div className="space-y-3">
            <p className="text-sm">1. Inquadra il QR con l'app di autenticazione. In alternativa inserisci questo codice: <span className="font-mono break-all" data-testid="twofa-secret">{setup.secret}</span></p>
            <p className="text-sm">2. Scrivi il codice a 6 cifre che compare nell'app.</p>
            <div className="flex gap-2"><Input inputMode="numeric" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="max-w-[160px]" data-testid="twofa-enable-code-input" />
              <button onClick={enable} className="btn-primary h-10 px-4" data-testid="twofa-enable-button">Attiva</button></div>
          </div>
        </div>
      ) : (
        <button onClick={start} className="btn-primary h-11 px-5" data-testid="twofa-start-button">Configura ora</button>
      )}
    </section>
  );
}

export default function Account() {
  const { user } = useAuth();
  return (
    <div className="space-y-8 max-w-3xl">
      <div className="fade-up">
        <p className="eyebrow">Sicurezza</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Il mio account</h1>
        <p className="text-sm text-[var(--muted)] mt-2" data-testid="account-role">{user.name || user.username} · {ROLE_LABELS[user.role]} — {ROLE_HINTS[user.role]}</p>
      </div>
      <PasswordCard />
      <TwoFactorCard />
    </div>
  );
}
